import { Sunrise, Moon, Volume2 } from "lucide-react";

const _toneClass = (tone) => {
  if (tone === "gold") return "text-gold";
  if (tone === "amber") return "text-amber-400";
  return "text-white/80";
};

/**
 * BriefingCard — renders the morning- or wrap-up-briefing message bubble
 * (header chip with greeting + optional grade pill + Hear-it button,
 * KPI grid, and clickable suggestion chips).
 *
 * Designed to be mounted INSIDE a message bubble, on top of the regular
 * text rendering. Receives the briefing payload (kpis, suggestions, kind,
 * score_grade), the speak helpers, and a callback that populates the
 * input when the user taps a suggestion.
 */
export default function BriefingCard({ briefing, content, speakText, voiceEnabled, setVoiceEnabled, voiceEnabledRef, speaking, synthAvailable }) {
  if (!briefing) return null;

  const handleSpeakClick = () => {
    if (!voiceEnabled) {
      setVoiceEnabled(true);
      // Small delay so the state flip propagates before speakText reads it
      setTimeout(() => {
        if (voiceEnabledRef) voiceEnabledRef.current = true;
        speakText(content);
      }, 50);
    } else {
      speakText(content);
    }
  };

  return (
    <>
      {/* Header chip */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[10px] font-body uppercase tracking-[0.18em] text-gold/70">
          {briefing.kind === "wrap_up" ? <Moon size={10} /> : <Sunrise size={10} />}
          {briefing.greeting || "Briefing"}
          {briefing.kind === "wrap_up" && briefing.score_grade && briefing.score_grade !== "—" && (
            <span className="ml-1 px-1.5 py-0.5 bg-gold/20 text-gold text-[9px] font-heading">
              {briefing.score_grade}
            </span>
          )}
        </div>
        {synthAvailable && (
          <button
            onClick={handleSpeakClick}
            className={`flex items-center gap-1 text-[9px] font-body px-1.5 py-0.5 transition-colors ${speaking ? "text-gold animate-pulse" : "text-white/40 hover:text-gold"}`}
            title="Hear this briefing"
            data-testid="briefing-speak"
          >
            <Volume2 size={10} /> {speaking ? "Speaking…" : "Hear it"}
          </button>
        )}
      </div>
    </>
  );
}

/**
 * BriefingDetails — KPI grid + suggestion list. Rendered AFTER the message
 * text so the textual summary reads naturally before the cards appear.
 */
export function BriefingDetails({ briefing, onSuggestionClick }) {
  if (!briefing) return null;
  return (
    <>
      {briefing.kpis?.length > 0 && (
        <div className="grid grid-cols-2 gap-1.5 mt-3 pt-3 border-t border-gold/15" data-testid="briefing-kpis">
          {briefing.kpis.slice(0, 6).map((k, idx) => (
            <div key={idx} className="bg-black/40 px-2 py-1.5 border border-white/5">
              <p className={`font-heading text-base ${_toneClass(k.tone)}`}>{k.value}</p>
              <p className="text-white/40 text-[9px] font-body uppercase tracking-wider mt-0.5">{k.label}</p>
            </div>
          ))}
        </div>
      )}
      {briefing.suggestions?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gold/15" data-testid="briefing-suggestions">
          <p className="text-white/40 text-[9px] font-body uppercase tracking-wider mb-1.5">Suggested next move</p>
          <div className="flex flex-col gap-1">
            {briefing.suggestions.map((s, idx) => (
              <button
                key={idx}
                onClick={() => onSuggestionClick?.(s.prompt)}
                className="text-left text-[11px] font-body px-2 py-1.5 bg-white/[0.03] text-white/70 hover:bg-gold/15 hover:text-gold transition-colors border-l-2 border-gold/30 hover:border-gold"
                data-testid={`briefing-suggestion-${idx}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
